import type { AxiosRequestConfig } from 'axios'
import type { RequestError } from '@/types/api'

/** 后端敏感操作二次验证业务错误码（详见 spec §2.3）。 */
export const ADMIN_2FA_REQUIRED = 'ADMIN_2FA_REQUIRED'
export const ADMIN_2FA_INVALID = 'ADMIN_2FA_INVALID'

/** 两步式登录第二步挑战令牌业务错误码（后端 #1733，均为 HTTP 401）。 */
export const LOGIN_CHALLENGE_EXPIRED = 'CHALLENGE_EXPIRED'
export const LOGIN_CHALLENGE_INVALID = 'CHALLENGE_INVALID'

/** 挑战态自助绑定开关关闭业务错误码（后端 #1811，HTTP 403）。 */
export const ADMIN_2FA_SELF_ENROLL_DISABLED = 'ADMIN_2FA_SELF_ENROLL_DISABLED'

/** 敏感写操作的通用 2FA 选项；存在 twoFactorCode 时通过 header 传递。 */
export interface TwoFactorOptions {
  /** TOTP 6 位验证码。 */
  twoFactorCode?: string
}

/**
 * 为敏感写操作构造请求配置：统一关闭全局错误 toast（403/2FA 由调用方处理），
 * 并在带验证码时注入 X-Admin-2FA-Code header。供菜单/角色/管理员等接口复用。
 */
export function withTwoFactor(
  options?: TwoFactorOptions,
  base: AxiosRequestConfig = {}
): AxiosRequestConfig {
  const config: AxiosRequestConfig = { skipErrorToast: true, ...base }
  if (options?.twoFactorCode) {
    config.headers = { ...config.headers, 'X-Admin-2FA-Code': options.twoFactorCode }
  }
  return config
}

/**
 * 从标准化错误对象提取后端业务错误码（字符串）。
 * 兼容两种响应体形态：`error` 为字符串，或 `error.code`（见 spec §6 风险项）。
 */
export function getBusinessErrorCode(err: unknown): string | null {
  const raw = (err as RequestError | undefined)?.raw
  const data = (raw as { response?: { data?: { error?: unknown } } } | undefined)?.response?.data
  const error = data?.error
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'code' in error) {
    return String((error as { code: unknown }).code)
  }
  return null
}

function isHttp403(err: unknown): boolean {
  return (err as RequestError | undefined)?.code === 403
}

function isHttp401(err: unknown): boolean {
  return (err as RequestError | undefined)?.code === 401
}

/** 403 且业务码为 ADMIN_2FA_REQUIRED：需要弹验证码框。 */
export function is2faRequiredError(err: unknown): boolean {
  return isHttp403(err) && getBusinessErrorCode(err) === ADMIN_2FA_REQUIRED
}

/** 403 且业务码为 ADMIN_2FA_INVALID：验证码错误，框内重试。 */
export function is2faInvalidError(err: unknown): boolean {
  return isHttp403(err) && getBusinessErrorCode(err) === ADMIN_2FA_INVALID
}

/** 401 且业务码为 CHALLENGE_EXPIRED：登录挑战已过期，需退回密码步重新登录。 */
export function isLoginChallengeExpiredError(err: unknown): boolean {
  return isHttp401(err) && getBusinessErrorCode(err) === LOGIN_CHALLENGE_EXPIRED
}

/** 401 且业务码为 CHALLENGE_INVALID：挑战令牌无效 / 已消费 / 失败次数超限，需退回密码步。 */
export function isLoginChallengeInvalidError(err: unknown): boolean {
  return isHttp401(err) && getBusinessErrorCode(err) === LOGIN_CHALLENGE_INVALID
}

/** 403 且业务码为 ADMIN_2FA_SELF_ENROLL_DISABLED：自助绑定开关已关闭，需退回密码步提示重登。 */
export function isSelfEnrollDisabledError(err: unknown): boolean {
  return isHttp403(err) && getBusinessErrorCode(err) === ADMIN_2FA_SELF_ENROLL_DISABLED
}
